import reportApi from 'api/reportApi'

/**
 * Reports are backed by their OWN `createApi` instance (`reportApi`), and RTK
 * Query tags never cross createApi instances. So listing `'Report'` in a
 * booking / plot / broker mutation's `invalidatesTags` is a silent no-op —
 * nothing in those slices declares the tag, and the cached report keeps
 * serving pre-edit numbers until the page is reloaded.
 *
 * The only thing that works is dispatching `reportApi.util.invalidateTags`
 * from the mutation's `onQueryStarted`, which is what these two helpers do.
 * Reach for:
 *   - `invalidateReportCache(dispatch)` inside a slice's EXISTING
 *     `onQueryStarted` handler (bookings and plots already have one), and
 *   - `invalidateReportsOnSuccess` as the whole handler for a mutation that
 *     doesn't have one yet.
 *
 * `'Report'` is the single tag `generateReport` and `getDashboardSummary`
 * both provide, so one dispatch refreshes every open report AND the dashboard
 * KPIs. Rows currently on screen refetch in place; report types cached but not
 * on screen are marked stale and refetch the next time they're selected.
 */
export const invalidateReportCache = (
  dispatch: (action: unknown) => unknown
): void => {
  dispatch(reportApi.util.invalidateTags(['Report']))
}

/**
 * `onQueryStarted` handler that refreshes the report caches once the mutation
 * succeeds. A rejected mutation changed nothing on the server, so there is
 * nothing to refresh — hence the empty catch (awaiting `queryFulfilled`
 * without one would surface as an unhandled rejection).
 */
export const invalidateReportsOnSuccess = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<unknown>;
  }
): Promise<void> => {
  try {
    await queryFulfilled
    invalidateReportCache(dispatch)
  } catch {
    // Mutation failed — server state is unchanged, so the reports are still
    // accurate.
  }
}
